// Translations centralisées FR / EN pour Trois Couronnes Productions
// La structure data conserve l'image/email/trailer (langue-neutre) et duplique
// uniquement les champs textuels (title, format, status, pitch, etc.)

export const UI = {
  fr: {
    nav: {
      manifeste: "Manifeste",
      catalogue: "Catalogue",
      producteurs: "Producteurs",
      contact: "Contact",
    },
    hero: {
      titleLine1: "Nous fabriquons des récits,",
      titleAccent: "conçus pour marquer les esprits",
      titleTail: "et résonner au-delà des frontières.",
      subtitle: "Nous y attachons les talents qui bougent les lignes.",
      ctaCatalogue: "Découvrir le catalogue",
      ctaManifesto: "Notre manifeste",
      scroll: "Scroll",
    },
    manifeste: {
      eyebrow: "Manifeste",
      title: "Des fictions qui dérangent.",
      body: {
        before: "Trois Couronnes Productions développe des fictions ",
        accent: "qui bousculent les évidences",
        after:
          " et ouvrent des conversations inattendues, portées dès l'origine par des auteurs singuliers et des talents d'exception.",
      },
      pillars: [
        { k: "Cinéma", v: "Longs métrages" },
        { k: "Télévision", v: "Séries originales" },
        { k: "Plateformes", v: "Formats hybrides" },
      ],
    },
    catalogue: {
      eyebrow: "Catalogue",
      description:
        "Séries et longs métrages développés et produits par Trois Couronnes Productions, à la croisée de la comédie, du thriller et de la satire contemporaine.",
      tabs: {
        developpement: "En développement",
        diffusee: "Diffusée",
      },
      cta: "Lire le pitch",
    },
    modal: {
      pitchLabel: "Pitch",
      productionPrefix: "Production",
      genrePrefix: "Genre",
      trailer: "Voir la bande-annonce",
      close: "Fermer",
    },
    producers: {
      eyebrow: "Producteurs associés",
      title: "Trois regards. Une exigence.",
    },
    contact: {
      eyebrow: "Contact",
      title: "Parlons d'un récit.",
      description:
        "Auteurs, agents, diffuseurs — écrivez-nous directement. Chaque projet commence par une conversation.",
      copyright: "Tous droits réservés",
      location: "Paris · France",
      legalLink: "Mentions légales",
    },
    language: {
      label: "Langue",
      fr: "FR",
      en: "EN",
    },
  },
  en: {
    nav: {
      manifeste: "Manifesto",
      catalogue: "Catalogue",
      producteurs: "Producers",
      contact: "Contact",
    },
    hero: {
      titleLine1: "We craft stories,",
      titleAccent: "built to leave a mark",
      titleTail: "and resonate beyond borders.",
      subtitle: "We bring together the talents who move the lines.",
      ctaCatalogue: "Explore the slate",
      ctaManifesto: "Our manifesto",
      scroll: "Scroll",
    },
    manifeste: {
      eyebrow: "Manifesto",
      title: "Fiction that disturbs.",
      body: {
        before: "Trois Couronnes Productions develops fiction ",
        accent: "that shakes up the obvious",
        after:
          " and opens unexpected conversations, driven from the outset by singular authors and exceptional talents.",
      },
      pillars: [
        { k: "Cinema", v: "Feature films" },
        { k: "Television", v: "Original series" },
        { k: "Platforms", v: "Hybrid formats" },
      ],
    },
    catalogue: {
      eyebrow: "Slate",
      description:
        "Series and feature films developed and produced by Trois Couronnes Productions, at the crossroads of comedy, thriller and contemporary satire.",
      tabs: {
        developpement: "In development",
        diffusee: "Released",
      },
      cta: "Read the pitch",
    },
    modal: {
      pitchLabel: "Pitch",
      productionPrefix: "Production",
      genrePrefix: "Genre",
      trailer: "Watch the trailer",
      close: "Close",
    },
    producers: {
      eyebrow: "Associate producers",
      title: "Three visions. One standard.",
    },
    contact: {
      eyebrow: "Contact",
      title: "Let's talk about a story.",
      description:
        "Writers, agents, broadcasters — write to us directly. Every project starts with a conversation.",
      copyright: "All rights reserved",
      location: "Paris · France",
      legalLink: "Legal notice",
    },
    language: {
      label: "Language",
      fr: "FR",
      en: "EN",
    },
  },
};

// ---------- PROJECTS ----------
// Base langue-neutre (image, trailer, id, ordre)
const PROJECT_META = [
  {
    id: "le-paradis",
    image: "/projects/le-paradis.webp",
    aspect: "landscape",
    productionFr: "Trois Couronnes Productions",
    productionEn: "Trois Couronnes Productions",
  },
  {
    id: "itineraire-dun-con",
    image: "/projects/itineraire-dun-con.webp",
    aspect: "portrait",
    productionFr: "Coproduction Trois Couronnes Productions / Dark Smile Productions",
    productionEn: "Co-production Trois Couronnes Productions / Dark Smile Productions",
  },
  {
    id: "vixens",
    image: "/projects/vixens.webp",
    aspect: "portrait",
    productionFr: "Trois Couronnes Productions",
    productionEn: "Trois Couronnes Productions",
  },
  {
    id: "or-de-lui",
    image: "/projects/or-de-lui.jpeg",
    aspect: "portrait",
    trailer: "https://www.youtube.com/watch?v=aHwUjvxLD4A",
    productionFr: "France Télévisions / Calt Story",
    productionEn: "France Télévisions / Calt Story",
  },
  {
    id: "triade",
    image: "/projects/triade.webp",
    aspect: "landscape",
    fit: "contain",
    productionFr: "Trois Couronnes Productions",
    productionEn: "Trois Couronnes Productions",
  },
  {
    id: "le-secret-de-gabrielle",
    image: "/projects/le-secret-de-gabrielle.webp",
    aspect: "landscape",
    productionFr: "Trois Couronnes Productions",
    productionEn: "Trois Couronnes Productions",
  },
  {
    id: "surtout-pas-toi",
    image: "/projects/surtout-pas-toi.webp",
    aspect: "landscape",
    productionFr: "Trois Couronnes Productions",
    productionEn: "Trois Couronnes Productions",
  },
  {
    id: "sous-un-ciel-bleu",
    image: "/projects/sous-un-ciel-bleu.webp",
    aspect: "landscape",
    productionFr: "Trois Couronnes Productions",
    productionEn: "Trois Couronnes Productions",
  },
  {
    id: "primitif",
    image: "/projects/primitif.webp",
    aspect: "portrait",
    productionFr: "Trois Couronnes Productions",
    productionEn: "Trois Couronnes Productions",
  },
  {
    id: "la-maupin",
    image: "/projects/la-maupin.webp",
    aspect: "portrait",
    productionFr: "Trois Couronnes Productions · France / Royaume-Uni",
    productionEn: "Trois Couronnes Productions · France / United Kingdom",
  },
];

// Textes par langue, indexés par id
const PROJECT_TEXTS = {
  fr: {
    triade: {
      title: "Triade",
      format: "Long métrage",
      status: "En développement",
      genre: "Comédie",
      pitch:
        "Deux ex-meilleures amies que tout oppose, une analyste du crime organisé et une fumeuse de weed au RSA, renversent accidentellement le fils d'un puissant chef de la mafia chinoise… qui se réveille avec le cerveau d'un enfant de 8 ans.",
    },
    "or-de-lui": {
      title: "Or de lui",
      format: "Série · 10 × 26 min",
      status: "Diffusée",
      genre: "Comédie satirique",
      pitch:
        "Joseph travaille dans une petite PME de banlieue où il s'ennuie et n'est ni écouté, ni respecté par ses collègues. Sa vie personnelle n'est guère plus enthousiasmante avec une femme qui ne l'aime plus et commence à le tromper. Un jour, il commence à déféquer de l'or.",
    },
    primitif: {
      title: "Primitif",
      format: "Série · 8 × 30 min",
      status: "En développement",
      genre: "Comédie noire",
      pitch:
        "Lorsqu'une femme donne naissance à un bébé singe, un scientifique marginalisé tente de comprendre la menace qui pèse sur l'humanité.",
    },
    vixens: {
      title: "Vixens",
      format: "Série · 8 × 30 min",
      status: "En développement",
      genre: "Comédie",
      pitch:
        "Célia, 29 ans, est prête à tout pour sauver son agence de Vixens. Mais quand un coup d'éclat inattendu la propulse avec ses quatre danseuses sous les projecteurs du rap, elle se retrouve à la tête d'un phénomène qu'elle n'avait pas vu venir. Et le succès pourrait bien avoir un prix.",
    },
    "itineraire-dun-con": {
      title: "Itinéraire d'un con",
      format: "Long métrage",
      status: "En développement",
      genre: "Comédie",
      pitch:
        "Jacques Billon, 37 ans, a un sentiment récurent depuis sa jeunesse, le sentiment d'être parfois pris pour un con. Jusqu'au jour où il décide de sauter le pas en passant un test de Q.I pour en avoir le cœur net. Le verdict tombe, 83, soit 2 points en dessous de la moyenne. Jacques est débile léger. Mais il ne l'entend pas de cette oreille et va tout faire pour se prouver à lui et aux autres qu'il n'est pas con. Cette quête à l'intelligence va le forcer à sortir de sa zone de confort et lui offrir un destin extraordinaire.",
    },
    "surtout-pas-toi": {
      title: "Surtout pas toi",
      format: "Long métrage",
      status: "En développement",
      genre: "Comédie romantique",
      pitch:
        "Après un coup de foudre fulgurant, Emma et Marco découvrent en moins de quarante-huit heures qu'ils ne se supportent pas. Quinze ans plus tard, alors qu'ils s'étaient juré de ne plus jamais se revoir, le destin les oblige à reprendre la route ensemble.",
    },
    "le-secret-de-gabrielle": {
      title: "Le Secret de Gabrielle",
      format: "Mini-série · 4 × 52 min",
      status: "En développement",
      genre: "Romance · Thriller",
      pitch:
        "Le Secret de Gabrielle est un thriller romantique situé sur la côte basque. Gabrielle Augier, mère célibataire, tient un petit restaurant de plage sur une côte paisible. Quand son fils Jojo commence à s'interroger sur son père absent, elle invente un navigateur disparu en mer, Marc Bagorry. Mais lorsque Marc réapparaît, amnésique, le mensonge de Gabrielle prend vie.",
    },
    "la-maupin": {
      title: "La Maupin",
      format: "Long métrage",
      status: "En développement",
      genre: "Romance · Drame",
      pitch:
        "France, XVIIe siècle. Un chasseur de têtes traque Julie d'Aubigny, alias LA MAUPIN, célèbre duelliste et cantatrice devenue une légende vivante après avoir défié les hommes, l'Église et le royaume. Lorsqu'il trouve refuge pour la nuit chez une étrange vieille femme, celle-ci prétend avoir été l'amante abandonnée de La Maupin.",
    },
    "sous-un-ciel-bleu": {
      title: "Sous un ciel bleu",
      format: "Série · 8 × 30 min",
      status: "En développement",
      genre: "Comédie dramatique",
      pitch:
        "Michaël Giraudet avait quinze ans quand il est entré dans « Sous un ciel bleu ». Vingt-cinq ans plus tard, le patron de la chaîne, jaloux, plonge son personnage dans le coma. Largué, ruiné, il devient prof de théâtre dans le lycée de sa mère, au moment où une équipe de documentaire s'y installe pour un an, menée par une ancienne camarade de classe qu'il a oubliée. Elle, non.\n\nMichaël y voit l'occasion de revenir dans la lumière. Les caméras ne le lâchent plus, jusque dans son intimité. Son obsession : sortir du coma avant d'être débranché. Encore faudrait-il qu'il se réveille dans la vraie vie.",
    },
    "le-paradis": {
      title: "Le Paradis",
      format: "Long métrage",
      status: "En développement",
      genre: "Comédie",
      pitch:
        "Un couple catholique bourgeois déclassé élève ses cinq enfants dans une misère soigneusement dissimulée à leur entourage : costumes repassés, banc d'église réservé, façade impeccable. Quand l'oncle déviant de la famille leur lègue un club libertin florissant, avec clause contractuelle leur interdisant de toucher à la moindre ampoule, tout ce qu'ils ont passé leur vie à taire, refouler et interdire va remonter à la surface, dans leur couple, avec leurs enfants, et devant toute leur paroisse.",
    },
  },
  en: {
    triade: {
      title: "Triad",
      format: "Feature film",
      status: "In development",
      genre: "Comedy",
      pitch:
        "Two former best friends who are polar opposites, an organized crime analyst and a pot-smoking welfare queen, accidentally run over the son of a powerful Chinese mafia boss… who wakes up with the mind of an 8-year-old.",
    },
    "or-de-lui": {
      title: "Or de lui",
      format: "Series · 10 × 26 min",
      status: "Released",
      genre: "Satirical comedy",
      pitch:
        "Joseph works in a small suburban company where he is bored and neither listened to nor respected by his colleagues. His personal life is hardly more exciting: his wife no longer loves him and is starting to cheat on him. One day, he starts defecating gold.",
    },
    primitif: {
      title: "Primitive",
      format: "Series · 8 × 30 min",
      status: "In development",
      genre: "Dark comedy",
      pitch:
        "When a woman gives birth to a baby monkey, a marginalised scientist tries to understand the threat looming over humanity.",
    },
    vixens: {
      title: "Vixens",
      format: "Series · 8 × 30 min",
      status: "In development",
      genre: "Comedy",
      pitch:
        "Célia, 29, is ready to do whatever it takes to save her Vixens agency. But when an unexpected stunt thrusts her and her four dancers into the spotlight of the rap scene, she finds herself at the head of a phenomenon she never saw coming. And success might just come at a price.",
    },
    "itineraire-dun-con": {
      title: "Itinéraire d'un con",
      format: "Feature film",
      status: "In development",
      genre: "Comedy",
      pitch:
        "Jacques Billon, 37, has had a recurring feeling since his youth: the feeling that people sometimes take him for a fool. Until the day he decides to take the plunge and sit an IQ test to find out for sure. The verdict falls: 83, two points below average. Jacques is mildly intellectually disabled. But he refuses to accept it and will do everything he can to prove, to himself and to others, that he is not a fool. This quest for intelligence will force him out of his comfort zone and offer him an extraordinary destiny.",
    },
    "surtout-pas-toi": {
      title: "Surtout pas toi",
      format: "Feature film",
      status: "In development",
      genre: "Romantic comedy",
      pitch:
        "After a whirlwind love at first sight, Emma and Marco realise within less than forty-eight hours that they can't stand each other. Fifteen years later, having sworn never to see each other again, fate forces them to hit the road together.",
    },
    "le-secret-de-gabrielle": {
      title: "Gabrielle's Secret",
      format: "Mini-series · 4 × 52 min",
      status: "In development",
      genre: "Romance · Thriller",
      pitch:
        "A romantic thriller set on the Basque coast. Gabrielle Augier, a single mother, runs a small seaside restaurant. When her son Jojo starts asking her questions about his absent father, she invents a story about a sailor who went missing at sea: Marc Bagorry. But when Marc reappears, suffering from amnesia, Gabrielle's lie comes to life.",
    },
    "la-maupin": {
      title: "La Maupin",
      format: "Feature film",
      status: "In development",
      genre: "Romance · Drama",
      pitch:
        "France, 17th century. A bounty hunter is tracking down Julie d'Aubigny, alias LA MAUPIN, a famous duelist and opera singer who became a living legend after defying men, the Church, and the kingdom. When he finds shelter for the night with a strange old woman, she claims to have been La Maupin's rejected lover.",
    },
    "sous-un-ciel-bleu": {
      title: "Sous un ciel bleu",
      format: "Series · 8 × 30 min",
      status: "In development",
      genre: "Dramatic comedy",
      pitch:
        "Michaël Giraudet was fifteen when he joined the soap opera \"Sous un ciel bleu\". Twenty-five years later, the jealous head of the network puts his character into a coma. Dumped and broke, he becomes a drama teacher at his mother's high school, just as a documentary crew moves in for a year, led by a former classmate he has forgotten. She hasn't.\n\nMichaël sees it as a chance to return to the spotlight. The cameras never leave him, right into his private life. His obsession: to come out of the coma before being unplugged. If only he could wake up in real life.",
    },
    "le-paradis": {
      title: "The Paradise Club",
      format: "Feature film",
      status: "In development",
      genre: "Comedy",
      pitch:
        "A bourgeois Catholic couple who have fallen from grace, raise their five children in poverty that is carefully concealed from those around them: pressed suits, a reserved church pew, and a flawless facade. When the family's eccentric uncle bequeaths them a thriving swingers' club, with a contractual clause forbidding them from touching even a single light bulb, everything they have spent their lives keeping quiet, repressing, and forbidding will resurface, in their marriage, with their children, and before their entire parish.",
    },
  },
};

export function getProjects(lang) {
  return PROJECT_META.map((meta) => {
    const t = PROJECT_TEXTS[lang][meta.id];
    return {
      id: meta.id,
      image: meta.image,
      aspect: meta.aspect,
      fit: meta.fit || "cover",
      trailer: meta.trailer,
      production: lang === "fr" ? meta.productionFr : meta.productionEn,
      title: t.title,
      format: t.format,
      status: t.status,
      genre: t.genre,
      pitch: t.pitch,
    };
  });
}

// Mapping pour tabs (status par langue)
export const STATUS_KEYS = {
  fr: { developpement: "En développement", diffusee: "Diffusée" },
  en: { developpement: "In development", diffusee: "Released" },
};

// ---------- PRODUCERS ----------
const PRODUCER_META = [
  {
    id: "clemence-terrier",
    name: "Clémence Terrier",
    image: "/team/clemence.webp",
    email: "clemence@troiscouronnesproductions.com",
  },
  {
    id: "nabiha-akkari",
    name: "Nabiha Akkari",
    image: "/team/nabiha.jpg",
    email: "nabiha@troiscouronnesproductions.com",
  },
  {
    id: "baptiste-lorber",
    name: "Baptiste Lorber",
    image: "/team/baptiste.jpg",
    email: "baptiste@troiscouronnesproductions.com",
  },
];

const PRODUCER_TEXTS = {
  fr: {
    "clemence-terrier": {
      role: "Directrice générale & productrice associée",
      bio: "Formée à la production et à la direction littéraire entre Paris et Los Angeles, Clémence évolue depuis plus de douze ans dans la fiction française et internationale. Après plusieurs expériences en production, elle rejoint Lagardère Studios, où elle accompagne notamment les séries Clem et Philharmonia, avant de prendre la direction littéraire de Merlin Productions puis de Vema Production (Mediawan). Elle y développe et supervise des projets pour France Télévisions, TF1, M6, Arte et les plateformes.\n\nElle poursuit ensuite son parcours au sein de Federation Studios, puis de Syzygy Entertainment, où elle développe et produit des séries premium destinées au marché international. Elle produit notamment Okavango, thriller anglophone réalisé par Jérôme Salle pour France Télévisions, et accompagne le développement de projets avec Amazon et Apple. Portée par une forte exigence éditoriale, elle s'attache à faire émerger des récits ambitieux et à accompagner des auteurs et des créateurs aux voix singulières, de la naissance du projet jusqu'à sa mise en production. Au sein de TROIS COURONNES, elle occupe les fonctions de directrice générale et productrice.",
    },
    "nabiha-akkari": {
      role: "Productrice associée & présidente",
      bio: "Diplômée en journalisme et formée à la dramaturgie, Nabiha a développé des séries comme scénariste pour Arte, France Télévisions et M6. Après plus de quinze ans dans l'audiovisuel comme comédienne à l'international, autrice et réalisatrice, représentée en Grande-Bretagne et en Italie, elle développe aujourd'hui pour TROIS COURONNES des projets originaux pour le cinéma, la télévision et les plateformes, avec une forte sensibilité aux écritures adaptables et aux coproductions internationales. Polyglotte, elle construit des récits pensés dès l'origine pour circuler entre les langues, les formats et les territoires.\n\nSes récits explorent les destins féminins, la diversité culturelle et les enjeux contemporains à travers des histoires sensibles et ancrées dans le réel.",
    },
    "baptiste-lorber": {
      role: "Producteur associé",
      bio: "Initialement révélé au grand public pour son travail pionnier sur YouTube avec 10minutesaperdre.fr, Baptiste s'est imposé au fil des années comme un créateur polyvalent, naviguant entre web, télévision et cinéma. Co-créateur de la chaîne influente Bapt&Gaël et membre du collectif Studio Bagel, il développe une écriture et une mise en scène à la croisée de la comédie et de la narration contemporaine. Récemment, il a confirmé son virage vers la fiction avec la création et la réalisation de la série « Or de lui ». Également auteur de deux romans, Itinéraire d'un con et Les Aventures de Jacques Bertincauslet, il poursuit une exploration des formes hybrides entre humour et récit. Au sein de Trois Couronnes Productions, il développe et produit des récits qui font parler, portés par une exigence d'écriture et un intérêt marqué pour les zones de friction entre comédie, société et narration contemporaine.",
    },
  },
  en: {
    "clemence-terrier": {
      role: "General Manager & Associate Producer",
      bio: "Trained in production and literary development between Paris and Los Angeles, Clémence has spent more than twelve years working in French and international fiction. After several roles in production, she joined Lagardère Studios, where she notably worked on the series Clem and Philharmonia, before becoming head of literary development at Merlin Productions and then Vema Production (Mediawan). There she developed and supervised projects for France Télévisions, TF1, M6, Arte and the streaming platforms.\n\nShe went on to work at Federation Studios and then Syzygy Entertainment, where she develops and produces premium series for the international market. Among other projects, she produced Okavango, an English-language thriller directed by Jérôme Salle for France Télévisions, and supported the development of projects with Amazon and Apple. Driven by a strong editorial standard, she is committed to bringing ambitious stories to life and to nurturing authors and creators with singular voices, from a project's inception through to production. At TROIS COURONNES, she serves as General Manager and Producer.",
    },
    "nabiha-akkari": {
      role: "Associate producer & President",
      bio: "With a degree in journalism and training in screenwriting, Nabiha has developed series as a screenwriter for Arte, France Télévisions, and M6. After more than fifteen years in the audiovisual industry as an international actress, writer, and director, represented in the UK and Italy, she now develops original projects for TROIS COURONNES for film, television, and streaming platforms, with a strong focus on adaptable scripts and international co-productions. A polyglot, she crafts narratives designed from the outset to transcend languages, formats, and borders.\n\nHer stories explore women's lives, cultural diversity, and contemporary issues through sensitive narratives rooted in reality.",
    },
    "baptiste-lorber": {
      role: "Associate producer",
      bio: "Initially revealed to a wider audience through his pioneering work on YouTube with 10minutesaperdre.fr, Baptiste has established himself over the years as a versatile creator, moving between web, television and cinema. Co-creator of the influential channel Bapt&Gaël and member of the Studio Bagel collective, he has developed a writing and directing style at the crossroads of comedy and contemporary storytelling. He recently confirmed his shift towards fiction with the creation and direction of the series \"Or de lui\". Also the author of two novels — Itinéraire d'un con and Les Aventures de Jacques Bertincauslet — he continues to explore hybrid forms between humour and narrative. At Trois Couronnes Productions, he develops and produces stories that get people talking, driven by a demanding writing standard and a marked interest in the friction zones between comedy, society and contemporary narrative.",
    },
  },
};

export function getProducers(lang) {
  return PRODUCER_META.map((meta) => {
    const t = PRODUCER_TEXTS[lang][meta.id];
    return {
      id: meta.id,
      name: meta.name,
      image: meta.image,
      email: meta.email,
      role: t.role,
      bio: t.bio,
    };
  });
}
